import { Injectable } from '@angular/core';
import { SessionStorageService } from '../services/session-storage.service';
import { TenantScopeService } from '../company/tenant-scope.service';
import { RouteTenantScope, ScopedRole } from '../company/tenant-access.model';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {

  constructor(
    private sessionStorageService: SessionStorageService,
    private tenantScopeService: TenantScopeService
  ) { }

  hasPermission(permissionCode: string, requiredScope?: RouteTenantScope): boolean {
    if (!permissionCode) {
      return true;
    }

    if (!this.tenantScopeService.routeScopeAllowed(requiredScope)) {
      return false;
    }

    if (this.isAdmin()) {
      return true;
    }

    return this.getPermissionCodes().has(permissionCode);
  }

  hasAnyPermission(permissionCodes: string[], requiredScope?: RouteTenantScope): boolean {
    if (!permissionCodes?.length) {
      return true;
    }

    if (!this.tenantScopeService.routeScopeAllowed(requiredScope)) {
      return false;
    }

    if (this.isAdmin()) {
      return true;
    }

    const userPermissions = this.getPermissionCodes();
    return permissionCodes.some(permissionCode => userPermissions.has(permissionCode));
  }

  hasRole(roleName: string): boolean {
    if (!roleName) {
      return true;
    }

    return this.getRoleNames().some(role => role === roleName);
  }

  hasAccessibleGroupPermission(permissionCode: string, memberCompanyId?: number): boolean {
    return this.tenantScopeService.getAccessibleCompanyGroups().some(group =>
      (memberCompanyId == null || (group.memberCompanies ?? []).some(company => company.id === memberCompanyId))
      && (group.roles ?? []).some(role => (role.permissionCodes ?? []).includes(permissionCode))
    );
  }

  isAdmin(): boolean {
    return this.getRoleNames().some(role => {
      const normalizedRole = role.toUpperCase();
      return normalizedRole.includes('ADMIN') || normalizedRole.includes('OWNER') || normalizedRole.includes('SUPER');
    });
  }

  getPermissionCodes(): Set<string> {
    const scopedPermissionCodes = this.tenantScopeService.getActivePermissionCodes();
    if (scopedPermissionCodes.size || !this.shouldUseLegacyCustomerRoles()) {
      return scopedPermissionCodes;
    }

    return this.getLegacyPermissionCodes();
  }

  hasTenantAccessContext(): boolean {
    const activeScope = this.tenantScopeService.getActiveScope();
    if (activeScope) {
      return true;
    }

    const summary = this.tenantScopeService.getAccessSummary();
    return !!(summary?.companies?.length || summary?.companyGroups?.length || this.sessionStorageService.getCompany()?.id);
  }

  private getLegacyPermissionCodes(): Set<string> {
    const customer = this.sessionStorageService.getCustomer();
    const permissionCodes = new Set<string>();

    if (!customer?.roles?.length) {
      return permissionCodes;
    }

    for (const role of customer.roles as any[]) {
      for (const permission of role?.permissions ?? []) {
        const permissionCode = permission?.permissionCode ?? permission;
        if (typeof permissionCode === 'string' && permissionCode.trim()) {
          permissionCodes.add(permissionCode);
        }
      }
    }

    return permissionCodes;
  }

  private getRoleNames(): string[] {
    const scopedRoleNames = this.tenantScopeService.getActiveRoles()
      .map((role: ScopedRole) => role?.roleName)
      .filter((roleName: unknown): roleName is string => typeof roleName === 'string' && roleName.trim().length > 0);

    if (scopedRoleNames.length || !this.shouldUseLegacyCustomerRoles()) {
      return scopedRoleNames;
    }

    const customer = this.sessionStorageService.getCustomer();
    return (customer?.roles ?? [])
      .map((role: any) => role?.roleName)
      .filter((roleName: unknown): roleName is string => typeof roleName === 'string' && roleName.trim().length > 0);
  }

  private shouldUseLegacyCustomerRoles(): boolean {
    const activeScope = this.tenantScopeService.getActiveScope();
    return !activeScope || (activeScope.type === 'COMPANY' && !!activeScope.legacyFallback);
  }
}
